import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, XIcon, PhoneIcon } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../../i18n/LanguageContext';
import { COMPANY } from '../../data/company';
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  const {
    t
  } = useLanguage();
  const nav = [{
    label: t.nav.home,
    to: '/'
  }, {
    label: t.nav.about,
    to: '/about'
  }, {
    label: t.nav.services,
    to: '/services'
  }, {
    label: t.nav.howItWorks,
    to: '/how-it-works'
  }, {
    label: t.nav.faq,
    to: '/faq'
  }, {
    label: t.nav.contact,
    to: '/contact'
  }];
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);
  return <header className="sticky top-0 z-50 w-full" data-id="element-257" data-mp="bsox2gv6um07">
      <div className="hidden w-full bg-navy-950 lg:block" data-id="element-258" data-mp="gmjaj2k7a4qu">
        <div className="mx-auto flex max-w-container items-center justify-between px-6 py-2 text-[11px] tracking-[0.14em] text-navy-100/70" data-id="element-259" data-mp="lkl4wjn2tqhm">
          <p className="uppercase" data-id="element-260" data-mp="3k38huqrlxihk">{t.nav.topBar}</p>
          <div className="flex items-center gap-6" data-id="element-261" data-mp="3k4jjol1hsg61">
            <a href={`tel:${COMPANY.phoneHref}`} className="flex items-center gap-2 uppercase transition-colors hover:text-gold" data-id="element-262" data-mp="3p22z3o9xeyzk">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-263" data-mp="1x3n9wjvohfw" />
              {COMPANY.phone}
            </a>
            <LanguageSwitcher tone="light" data-id="element-264" data-mp="19941i1wrketv" />
          </div>
        </div>
      </div>

      <div className={`w-full border-b transition-all duration-300 ${scrolled ? 'border-navy-100 bg-white/95 backdrop-blur-md shadow-soft' : 'border-transparent bg-white'}`} data-id="element-265" data-mp="1db7o9movv5ug">
        <div className="mx-auto flex max-w-container items-center justify-between px-6 py-4" data-id="element-266" data-mp="35csa2w1p9fgv">
          <Link to="/" aria-label={`${COMPANY.legalName} — ${t.nav.home}`} data-id="element-267" data-mp="3jbrwdx1qpi9v">
            <Logo data-id="element-268" data-mp="3edqh7n7fq33o" />
          </Link>

          <nav className="hidden items-center gap-8 xl:flex" aria-label={t.nav.primary} data-id="element-269" data-mp="3eerqpn8syhja">
            {nav.map(item => <NavLink key={item.to} to={item.to} className={({
            isActive
          }) => `relative text-[13px] font-medium uppercase tracking-[0.12em] transition-colors ${isActive ? 'text-navy' : 'text-charcoal-light hover:text-navy'}`} data-id="element-270" data-mp="2mmsq2nkpuqkb">
                {({
              isActive
            }) => <span className="relative inline-block py-1" data-id="element-271" data-mp="22ytgvh5svrh0">
                    {item.label}
                    <span className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-300 ${isActive ? 'w-full' : 'w-0'}`} data-id="element-272" data-mp="igso3d99ialj" />
                  </span>}
              </NavLink>)}
          </nav>

          <div className="flex items-center gap-3" data-id="element-273" data-mp="eee5mu9i9cus">
            <div className="lg:hidden" data-id="element-274" data-mp="ed2xki911ixv">
              <LanguageSwitcher data-id="element-275" data-mp="124i7aw34v2j5" />
            </div>
            <Link to="/contact" className="hidden rounded-full bg-navy px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-white shadow-soft transition-all duration-300 hover:bg-navy-700 hover:shadow-lift sm:inline-block" data-id="element-276" data-mp="122nk1b4e2ybw">
              {t.nav.requestFunding}
            </Link>
            <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label={open ? t.nav.closeMenu : t.nav.openMenu} className="flex h-11 w-11 items-center justify-center rounded-full border border-navy-100 text-navy xl:hidden" data-id="element-277" data-mp="3cw9xwllz8rj8">
              {open ? <XIcon className="h-5 w-5" data-id="element-278" data-mp="3sd9in52gfwdc" /> : <MenuIcon className="h-5 w-5" data-id="element-279" data-mp="3od8w0x57cev7" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-280" data-mp="3je4kw0ypw728">
        {open && <motion.nav aria-label={t.nav.mobile} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.28,
        ease: 'easeInOut'
      }} className="overflow-hidden border-b border-navy-100 bg-white xl:hidden" data-id="element-281" data-mp="3jffpg2zzliue">
            <ul className="mx-auto max-w-container px-6 py-2" data-id="element-282" data-mp="5zritmtpfltc">
              {nav.map(item => <li key={item.to} className="border-b border-navy-50 last:border-0" data-id="element-283" data-mp="2vp3c2clz2twf">
                  <NavLink to={item.to} className={({
              isActive
            }) => `block py-4 text-sm uppercase tracking-[0.14em] ${isActive ? 'text-gold-dark' : 'text-navy'}`} data-id="element-284" data-mp="2qpqofpna1iss">
                    {item.label}
                  </NavLink>
                </li>)}
            </ul>
            <div className="px-6 pb-6" data-id="element-285" data-mp="2vbhvbo6u86vh">
              <Link to="/contact" className="block rounded-full bg-navy px-6 py-3 text-center text-[12px] font-semibold uppercase tracking-[0.14em] text-white" data-id="element-286" data-mp="2vcj6dsnustd7">
                {t.nav.requestFunding}
              </Link>
            </div>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}